// @ts-check
import { maxLength, minLength, required, url } from '@vuelidate/validators';
import { sortByField } from '@/Helpers/sorters';
import PromGroupModel from '@/Models/PromGroupModel';
import GroupMembershipModel from '@/Models/GroupMembershipModel';
import SymptomDefinitionModel from '@/Models/SymptomDefinitionModel';

export class GroupModel {
  constructor({
    Id,
    Name,
    Version,
    SymptomTypes,
    OrganizationId,
    PusherId,
    UrlSlug,
    LandingPageUrl,
    WelcomeText,
    GreetingText,
    IntroText,
    InfoText,
    FormTitle,
    FormText,
    FormButtonLabel,
    HelpUrl,
    Link1Url,
    Link1Text,
    Link2Url,
    Link2Text,
    MFStudySlug,
    MFGroupSyncActive,
    FirstReportSavedText,
    StatusReportTypes,
    PromGroups = [],
    SymptomDefinitions = [],
    StudyTasksPublished = false,
    GroupMemberships = []
  }) {
    this._modelName = 'Group';
    this.Id = Id || null;
    this.Name = Name;
    this.Version = Version;
    this.SymptomTypes = SymptomTypes;
    this.OrganizationId = OrganizationId;
    this.PusherId = PusherId;
    this.UrlSlug = UrlSlug;
    this.LandingPageUrl = LandingPageUrl;
    this.WelcomeText = WelcomeText;
    this.GreetingText = GreetingText;
    this.IntroText = IntroText;
    this.InfoText = InfoText;
    this.FormTitle = FormTitle;
    this.FormText = FormText;
    this.FormButtonLabel = FormButtonLabel;
    this.HelpUrl = HelpUrl;
    this.Link1Url = Link1Url;
    this.Link1Text = Link1Text;
    this.Link2Url = Link2Url;
    this.Link2Text = Link2Text;
    this.MFStudySlug = MFStudySlug;
    this.MFGroupSyncActive = MFGroupSyncActive;
    this.FirstReportSavedText = FirstReportSavedText;
    this.StatusReportTypes = StatusReportTypes;
    this.PromGroups = PromGroups.map(pg => new PromGroupModel(pg));
    this.SymptomDefinitions = SymptomDefinitions.map(
      sd => new SymptomDefinitionModel(sd)
    ).sort(sortByField('Index'));
    this.StudyTasksPublished = StudyTasksPublished;
    this.GroupMemberships = GroupMemberships.map(
      gm => new GroupMembershipModel(gm)
    );
  }

  get Saved() {
    return !!this.Version;
  }

  get PatientJoinedDate() {
    return this.GroupMemberships.find(gm => gm.RoleId === 'pat').JoinedDate;
  }

  get IsGlobal() {
    return this.OrganizationId == '0' && this.Id == '0';
  }

  get DisplayName() {
    return this.IsGlobal ? `Symptoms` : `${this.Name}`;
  }

  static get validations() {
    return {
      Id: {
        minLength: minLength(1),
        maxLength: maxLength(32),
        required
      },
      Name: {
        maxLength: maxLength(64),
        required
      },
      UrlSlug: {
        maxLength: maxLength(64)
      },
      LandingPageUrl: {
        maxLength: maxLength(255),
        url: url
      },
      WelcomeText: {
        maxLength: maxLength(1000)
      },
      GreetingText: {
        maxLength: maxLength(128)
      },
      IntroText: {
        maxLength: maxLength(1000)
      },
      InfoText: {
        maxLength: maxLength(1000)
      },
      FormTitle: {
        maxLength: maxLength(128)
      },
      FormText: {
        maxLength: maxLength(1000)
      },
      FormButtonLabel: {
        maxLength: maxLength(32)
      },
      HelpUrl: {
        maxLength: maxLength(255),
        url: url
      },
      Link1Url: {
        maxLength: maxLength(255),
        url: url
      },
      Link1Text: {
        maxLength: maxLength(64)
      },
      Link2Url: {
        maxLength: maxLength(255),
        url: url
      },
      Link2Text: {
        maxLength: maxLength(64)
      },
      FirstReportSavedText: {
        maxLength: maxLength(1000)
      },
      MFStudySlug: {
        maxLength: maxLength(40)
      },
      PromGroups: {},
      SymptomDefinitions: {},
      StudyTasksPublished: {}
    };
  }

  __copy() {
    return JSON.parse(JSON.stringify(this));
  }
}

export default GroupModel;
